import React, { FC, useCallback, useState } from 'react';
import { useClearRefinements, useRange } from 'react-instantsearch';
import { RefinementDropdown } from './RefinementDropdown';
import { FilterSelectionHeader } from '~/features/productList/components/FilterSelectionHeader';
import { RefinementProps } from './RefinementProps';
import { RefinementPrice } from './RefinementPrice';
import { RefinementSlider } from './RefinementSlider';
import RefinementList from './RefinementList';
import useOptimizedRefinementList from '~/shared/hooks/useOptimizedRefinementList';
import { useTranslation } from '~/shared/utils/translation';

const EFFECTIVE_PRICE_ATTRIBUTE = 'cust_effectivePrice';

type RefinementPriceDropdown = RefinementProps & {
    showCount?: boolean;
};

export const RefinementPriceDropdown: FC<RefinementPriceDropdown> = ({
    attribute,
    showCount = true,
    isVisible = true,
    label,
}: RefinementPriceDropdown) => {
    const { translate } = useTranslation();
    const [searchValue, setSearchValue] = useState('');
    const { allItems } = useOptimizedRefinementList({ attribute });
    const { start, range } = useRange({ attribute: EFFECTIVE_PRICE_ATTRIBUTE });
    const { refine: clearSelection } = useClearRefinements({
        includedAttributes: [attribute, EFFECTIVE_PRICE_ATTRIBUTE],
    });
    // Slider/min-max inputs refine cust_effectivePrice separately from the checkboxes, so count them too.
    const isRangeRefined =
        (start[0] !== -Infinity && start[0] !== range.min) ||
        (start[1] !== Infinity && start[1] !== range.max);
    const selectedCount =
        allItems.filter((item) => item.isRefined).length + (isRangeRefined ? 1 : 0);

    const onClose = (isOpen: boolean) => {
        if (!isOpen) setSearchValue('');
    };

    const onRefine = useCallback(() => {
        setSearchValue('');
    }, []);

    return (
        <RefinementDropdown
            label={label}
            count={selectedCount}
            searchValue={searchValue}
            onChange={onClose}
            topContent={
                <>
                    <RefinementSlider attribute={EFFECTIVE_PRICE_ATTRIBUTE} />
                    <RefinementPrice attribute={EFFECTIVE_PRICE_ATTRIBUTE} />
                    <FilterSelectionHeader
                        label={label ?? ''}
                        allLabel={translate('plp.facetSelection.price.all')}
                        selectedLabel={translate('plp.facetSelection.price.selected')}
                        selectedCount={selectedCount}
                        onClear={clearSelection}
                    />
                </>
            }
        >
            <RefinementList
                showCount={showCount}
                attribute={attribute}
                isVisible={isVisible}
                searchText={searchValue}
                onRefine={onRefine}
            />
        </RefinementDropdown>
    );
};
